import type { ReactNode, RefObject } from 'react';
import type { TFunction } from 'i18next';
import { MAX_QUANTITY, stockAmountIssue } from '@d-orca/shared/client/logistics';
import { parseNumber } from '@d-orca/shared/client/utils';
import type { DeliveredAmount } from '../deliveryAmounts';
import { StockEntryCombo } from './StockEntryCombo';
import styles from './StockEntryDialog.module.css';

interface IStockEntryAmountUnitFieldsProps {
  hasArrival: boolean;
  /** A sync's count may honestly be zero; a plain add may not. */
  isSync: boolean;
  amount: string;
  onAmountChange: (value: string) => void;
  amountClassName: string;
  fromDelivery: DeliveredAmount | null;
  amountSeedNote: ReactNode;
  unitComboRef: RefObject<HTMLDivElement | null>;
  unitComboOpen: boolean;
  onUnitBlurAway: () => void;
  unit: string;
  unitClassName: string;
  onUnitInputChange: (value: string) => void;
  onUnitInputClick: () => void;
  onUnitCaretClick: () => void;
  visibleUnitOptions: string[];
  onPickUnit: (u: string) => void;
  unitSeedNote: ReactNode;
  t: TFunction;
}

/** Amount + unit, beside the material in its row — unit is a localized
 *  combobox: typing filters the fixed options, but the value must resolve to
 *  a real option (no add-new here — a unit cannot be free text).
 *
 *  Not rendered at all under sync, where the amount is the computed merge
 *  handled by `StockEntrySyncFields`. */
export function StockEntryAmountUnitFields({
  hasArrival,
  isSync,
  amount,
  onAmountChange,
  amountClassName,
  fromDelivery,
  amountSeedNote,
  unitComboRef,
  unitComboOpen,
  onUnitBlurAway,
  unit,
  unitClassName,
  onUnitInputChange,
  onUnitInputClick,
  onUnitCaretClick,
  visibleUnitOptions,
  onPickUnit,
  unitSeedNote,
  t,
}: IStockEntryAmountUnitFieldsProps) {
  if (hasArrival) return null;
  // Why Save is off, said under the field — the amount's own rule (shared
  // with the Save check) rather than a button that just greys out. Blank says
  // nothing: an empty box needs no explanation.
  const issue = amount.trim() === '' ? null : stockAmountIssue(parseNumber(amount), isSync);
  // Cells of the Material row (`StockEntryMaterialField`), like the delivery
  // dialog's line: each box names itself by placeholder and a label kept for
  // assistive tech; the notes run full width underneath the row.
  return (
    <>
      <div className={styles.amountCell}>
        <label className="visually-hidden" htmlFor="stock-amount">
          {t('logistics.orderDialog.quantity')}
        </label>
        <input
          id="stock-amount"
          className={amountClassName}
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          inputMode="decimal"
          placeholder={t('logistics.orderDialog.quantity')}
          autoComplete="off"
        />
      </div>
      <div className={styles.unitCell}>
        <label className="visually-hidden" htmlFor="stock-unit">
          {t('logistics.stockDialog.unit')}
        </label>
        {/* The shared combo shell (`StockEntryCombo`). Typing filters the
            fixed options; only a real option can become the value (no
            add-new here — a unit cannot be free text). */}
        <StockEntryCombo
          id="stock-unit"
          comboRef={unitComboRef}
          open={unitComboOpen}
          onBlurAway={onUnitBlurAway}
          value={unit}
          className={unitClassName}
          placeholder={t('logistics.stockDialog.unit')}
          onInputChange={onUnitInputChange}
          onInputClick={onUnitInputClick}
          onCaretClick={onUnitCaretClick}
          options={visibleUnitOptions.map((unitOption) => ({ value: unitOption, label: unitOption }))}
          onPick={onPickUnit}
        />
      </div>
      <div className={styles.rowNotes}>
        {/* Only while the field still holds what the delivery said —
            the moment it is edited the note would be describing a
            number that is no longer there. */}
        {fromDelivery && amount === String(fromDelivery.quantity) ? (
          <span className={styles.seedHint}>
            {t('logistics.stockDialog.fromDelivery', { ref: fromDelivery.ref })}
          </span>
        ) : null}
        {issue ? (
          <span className={styles.fieldWarn} role="alert">
            {issue === 'too-large'
              ? t('logistics.stockDialog.amountTooLarge', { max: MAX_QUANTITY.toLocaleString('en-US') })
              : t(isSync ? 'logistics.stockDialog.amountInvalidSync' : 'logistics.stockDialog.amountInvalid')}
          </span>
        ) : null}
        {amountSeedNote}
        {unitSeedNote}
      </div>
    </>
  );
}
